import {
    SubjectReference,
    SubjectTags,
    SubjectWatchlist,
    Tag,
    TagInput,
    Watchlist,
    WatchlistInput,
    WatchlistWithItems,
    WorkspaceSubjectType,
} from '@/types';

import { apiClient } from '../http';

export const tagsService = {
    addAssignment: (tagId: string, input: SubjectReference) =>
        apiClient.post<{ id: string }, SubjectReference>(`/api/tags/${tagId}/assignments`, input),
    create: (input: TagInput) => apiClient.post<Tag, TagInput>('/api/tags', input),
    delete: (tagId: string) => apiClient.delete<void>(`/api/tags/${tagId}`),
    list: () => apiClient.get<Tag[]>('/api/tags'),
    listForSubject: (subject: Pick<SubjectReference, 'subjectId' | 'subjectType'>) =>
        apiClient.get<Tag[], typeof subject>('/api/tags/for-subject', { params: subject }),
    listForSubjects: async (subjects: Array<Pick<SubjectReference, 'subjectId' | 'subjectType'>>) => {
        const batches = Array.from({ length: Math.ceil(subjects.length / 100) }, (_, index) =>
            subjects.slice(index * 100, (index + 1) * 100),
        );
        const results = await Promise.all(
            batches.map((batch) =>
                apiClient.post<SubjectTags[], { subjects: typeof batch }>('/api/tags/for-subjects', {
                    subjects: batch,
                }),
            ),
        );

        return results.flat();
    },
    listSubjects: (
        tagIds: string[],
        subjectTypes: WorkspaceSubjectType | readonly WorkspaceSubjectType[],
        matchAll = false,
    ) => {
        const normalizedSubjectTypes = Array.from(
            new Set<WorkspaceSubjectType>(typeof subjectTypes === 'string' ? [subjectTypes] : Array.from(subjectTypes)),
        );

        return apiClient.post<
            SubjectReference[],
            { matchAll: boolean; subjectTypes: WorkspaceSubjectType[]; tagIds: string[] }
        >('/api/tags/subjects', { matchAll, subjectTypes: normalizedSubjectTypes, tagIds });
    },
    removeAssignment: (tagId: string, assignmentId: string) =>
        apiClient.delete<void>(`/api/tags/${tagId}/assignments/${assignmentId}`),
    update: (tagId: string, input: Partial<TagInput>) =>
        apiClient.put<Tag, Partial<TagInput>>(`/api/tags/${tagId}`, input),
};

export const watchlistsService = {
    addItem: (watchlistId: string, input: SubjectReference) =>
        apiClient.post<{ id: string }, SubjectReference>(`/api/watchlists/${watchlistId}/items`, input),
    create: (input: WatchlistInput) => apiClient.post<Watchlist, WatchlistInput>('/api/watchlists', input),
    delete: (watchlistId: string) => apiClient.delete<void>(`/api/watchlists/${watchlistId}`),
    get: (watchlistId: string) => apiClient.get<WatchlistWithItems>(`/api/watchlists/${watchlistId}`),
    list: () => apiClient.get<Watchlist[]>('/api/watchlists'),
    listForSubject: (subject: Pick<SubjectReference, 'subjectId' | 'subjectType'>) =>
        apiClient.get<SubjectWatchlist[], typeof subject>('/api/watchlists/for-subject', { params: subject }),
    listSubjects: (watchlistIds: string[], subjectType: WorkspaceSubjectType) =>
        apiClient.post<SubjectReference[], { subjectType: WorkspaceSubjectType; watchlistIds: string[] }>(
            '/api/watchlists/subjects',
            { subjectType, watchlistIds },
        ),
    removeItem: (watchlistId: string, itemId: string) =>
        apiClient.delete<void>(`/api/watchlists/${watchlistId}/items/${itemId}`),
    update: (watchlistId: string, input: Partial<WatchlistInput>) =>
        apiClient.put<Watchlist, Partial<WatchlistInput>>(`/api/watchlists/${watchlistId}`, input),
};
